import { getCookie, setCookie, removeCookie } from "@insite/client-framework/Common/Cookies";
import { SLO_COOKIE_NAME, SLO_CONFIG_URL } from "@thkStore/Common/Constants/AuthConstants";

interface SloConfig {
    enabled: boolean;
    cookieDomain: string;
    createCookie: boolean;
    localeValue: string;
}

const SLO_ACTIVE_KEY = "thk_slo_active";

let cachedConfig: SloConfig | null = null;
let inflight: Promise<SloConfig> | null = null;

async function fetchConfig(): Promise<SloConfig> {
    try {
        const response = await fetch(SLO_CONFIG_URL, {
            method: "GET",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
        });

        if (!response.ok) {
            console.warn("[THK SLO] Failed to fetch SLO config:", response.status);
            return { enabled: false, cookieDomain: "", createCookie: false, localeValue: "" };
        }

        const data = await response.json();
        cachedConfig = {
            enabled: data.enabled,
            cookieDomain: data.cookieDomain || "",
            createCookie: data.createCookie,
            localeValue: data.localeValue || "",
        };

        return cachedConfig;
    } catch (error) {
        console.warn("[THK SLO] Error fetching SLO config:", error);
        return { enabled: false, cookieDomain: "", createCookie: false, localeValue: "" };
    }
}

export async function getSloConfig(): Promise<SloConfig> {
    if (cachedConfig) {
        return cachedConfig;
    }
    if (inflight) {
        return inflight;
    }
    inflight = fetchConfig().finally(() => { inflight = null; });
    return inflight;
}

export function getSloSignInCookieValue(): string {
    return getCookie(SLO_COOKIE_NAME) || "";
}

export function hasSloSignInCookie(): boolean {
    return !!getCookie(SLO_COOKIE_NAME);
}

export function setSloSignInCookie(locale: string, domain: string): void {
    if (!domain) {
        return;
    }

    setCookie(SLO_COOKIE_NAME, locale, {
        domain,
        path: "/",
        expires: 365,
        sameSite: "Lax",
    });
}

export function removeSloSignInCookie(domain: string): void {
    if (!domain) {
        return;
    }

    removeCookie(SLO_COOKIE_NAME, {
        domain,
        path: "/",
    });
}

export function clearSloSessionState(): void {
    try {
        sessionStorage.removeItem(SLO_ACTIVE_KEY);
    } catch {
        // sessionStorage may not be available
    }
}

async function triggerSloLogout(): Promise<void> {
    clearSloSessionState();

    try {
        await fetch("/api/v1/sessions/current", { method: "DELETE", credentials: "include" });
    } catch (error) {
        console.warn("[THK SLO] Error deleting session:", error);
    }

    try {
        const response = await fetch("/api/v1/logout?returnUrl=/", {
            method: "GET",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
        });

        if (response.ok) {
            const data = await response.json();
            window.location.href = data.url || "/";
            return;
        }
    } catch (error) {
        console.warn("[THK SLO] Error calling logout API:", error);
    }

    window.location.href = "/";
}

export async function performSloCheck(isAuthenticated: boolean): Promise<void> {
    if (typeof window === "undefined") {
        return;
    }

    if (!isAuthenticated) {
        clearSloSessionState();
        return;
    }

    const config = await getSloConfig();
    if (!config.enabled || !config.cookieDomain || !config.localeValue) {
        return;
    }

    if (hasSloSignInCookie()) {
        const cookieValue = getSloSignInCookieValue();
        if (cookieValue !== config.localeValue) {
            await triggerSloLogout();
            return;
        }
        try {
            sessionStorage.setItem(SLO_ACTIVE_KEY, "true");
        } catch {
            // sessionStorage may not be available
        }
        return;
    }

    // Cookie is missing while user is authenticated
    let sloWasActive = false;
    try {
        sloWasActive = sessionStorage.getItem(SLO_ACTIVE_KEY) === "true";
    } catch {
        // sessionStorage may not be available
    }

    if (sloWasActive) {
        await triggerSloLogout();
        return;
    }

    // Cookie was never seen in this tab session — this is a fresh authentication
    if (config.createCookie) {
        setSloSignInCookie(config.localeValue, config.cookieDomain);
        try {
            sessionStorage.setItem(SLO_ACTIVE_KEY, "true");
        } catch {
            // sessionStorage may not be available
        }
    }
}
